import { useState } from 'react';

// material-ui
import { Theme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import Divider from '@mui/material/Divider';
import List from '@mui/material/List';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';

// third-party
import { FormattedMessage } from 'react-intl';

// project imports
import NavItem from './NavItem';
import NavGroup from './NavGroup';
import menuItems from 'menu-items';
import useAuth from 'hooks/useAuth';
import { SidebarGroup, SidebarGroupLabel, SidebarGroupContent, SidebarMenu, SidebarMenuItem } from '../../SidebarComponents';

import useConfig from 'hooks/useConfig';
import { HORIZONTAL_MAX_ITEM, MenuOrientation } from 'config';
import { useGetMenuMaster } from 'api/menu';

// types
import { NavItemType } from 'types/menu';
import useRules from 'hooks/useRules';

// ==============================|| DRAWER CONTENT - NAVIGATION ||============================== //

export default function Navigation() {
  const { menuOrientation } = useConfig();
  const { menuMaster } = useGetMenuMaster();
  const drawerOpen = menuMaster.isDashboardDrawerOpened;
  const downLG = useMediaQuery((theme: Theme) => theme.breakpoints.down('lg'));

  const [selectedID, setSelectedID] = useState<string | undefined>('');
  const [selectedItems, setSelectedItems] = useState<string | undefined>('');
  const [selectedLevel, setSelectedLevel] = useState<number>(0);
  const { user } = useAuth();

  const userRules = user?.rules ?? [];
  const isAdmin = userRules.includes('administrator');
  const { isExternalClient } = useRules();

  const routeRequiredRule: Record<string, string> = {
    '/users': 'users.read',
    '/roles': 'roles.read',
    '/departments': 'departments.read',
    '/company': 'company.read',
    '/clients': 'customers.read',
    '/products': 'products.read',
    '/taxes': 'taxes.read',
    '/orders': 'orders.read',
    '/orders/new': 'orders.create',
    '/products/new': 'products.create',
    '/billing': 'orders.read',
    '/logistics': 'stock.read',
    '/warehouse-map': 'stock.read',
    '/stock-check': 'stock.read',
    '/floor-workers': 'stock.read',
    '/contracts': 'projects-management.contracts.read',
    '/label-calibration': 'administrator',
    '/catalog-health': 'administrator',
    '/assistant': 'administrator'
  };

  const filterMenuChildren = (children: any[]): any[] =>
    (children || [])
      .map((c: any) => {
        if (c?.children?.length) {
          const nested = filterMenuChildren(c.children);
          if (!nested.length) return null;
          return { ...c, children: nested };
        }
        if (!c?.url) return c;
        if (isAdmin) return c;
        const needed = routeRequiredRule[c.url];
        if (!needed) return !isExternalClient ? c : null;

        // "Clientes" volta a aparecer pra role "Cliente" (17/08): um cliente B2B pode
        // cadastrar/ver os próprios clientes (B2C) — a listagem já é escopada pelo
        // backend (ver computeCustomerScope), então não vaza dados de outros clientes.
        if (
          isExternalClient &&
          (c.id === 'company' || c.url === '/company' || c.id === 'billing' || c.id === 'taxes')
        )
          return null;

        return userRules.includes(needed) ? c : null;
      })
      .filter(Boolean);

  const effectiveMenuItems = {
    ...menuItems,
    items: menuItems.items
      .map((g: any) => {
        if (!g?.children?.length) return g;
        const children = filterMenuChildren(g.children);
        if (!children.length) return null;
        return { ...g, children };
      })
      .filter(Boolean)
  };

  const isHorizontal = menuOrientation === MenuOrientation.HORIZONTAL && !downLG;

  const lastItem = isHorizontal ? HORIZONTAL_MAX_ITEM : null;
  let lastItemIndex = effectiveMenuItems.items.length - 1;
  let remItems: NavItemType[] = [];
  let lastItemId: string;

  //  first it checks menu item is more than giving HORIZONTAL_MAX_ITEM after that get lastItemid by giving horizontal max
  // item and it sets horizontal menu by giving horizontal max item lastly slice menuItem from array and set into remItems

  if (lastItem && lastItem < effectiveMenuItems.items.length) {
    lastItemId = effectiveMenuItems.items[lastItem - 1].id!;
    lastItemIndex = lastItem - 1;
    remItems = effectiveMenuItems.items.slice(lastItem - 1, effectiveMenuItems.items.length).map((item) => ({
      title: item.title,
      elements: item.children,
      icon: item.icon,
      ...(item.url && {
        url: item.url
      })
    }));
  }

  const navGroups = effectiveMenuItems.items.slice(0, lastItemIndex + 1).map((item, index) => {
    switch (item.type) {
      case 'group':
        if (item.url && item.id !== lastItemId) {
          return (
            <SidebarGroup key={item.id}>
              <SidebarMenu>
                <SidebarMenuItem>
                  <NavItem item={item} level={1} isParents setSelectedID={setSelectedID} />
                </SidebarMenuItem>
              </SidebarMenu>
            </SidebarGroup>
          );
        }

        return (
          <SidebarGroup key={item.id}>
            <SidebarGroupContent>
              <NavGroup
                setSelectedID={setSelectedID}
                setSelectedItems={setSelectedItems}
                setSelectedLevel={setSelectedLevel}
                selectedLevel={selectedLevel}
                selectedID={selectedID}
                selectedItems={selectedItems}
                lastItem={lastItem!}
                remItems={remItems}
                lastItemId={lastItemId}
                item={item}
              />
            </SidebarGroupContent>
          </SidebarGroup>
        );
      case 'item':
        return (
          <SidebarGroup key={item.id}>
            <SidebarMenu>
              <SidebarMenuItem>
                <NavItem item={item} level={1} isParents setSelectedID={setSelectedID} />
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroup>
        );
      default:
        return (
          <Typography key={item.id} variant="h6" color="error" align="center">
            <FormattedMessage id="fix-navigation-group" />
          </Typography>
        );
    }
  });

  return (
    <Box
      sx={{
        pt: drawerOpen ? (isHorizontal ? 0 : 2) : 0,
        ...(!isHorizontal && { '& > ul:first-of-type': { mt: 0 } }),
        display: isHorizontal ? { xs: 'block', lg: 'flex' } : 'block'
      }}
    >
      {navGroups}
    </Box>
  );
}
